import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  RESO_RESOURCES,
  ResoResource,
  ResourceEndpointVersion,
} from '../system/reso';
import { MediaField } from '../system/field';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { useDebugger } from './DebuggerContext';
import {
  MediaCategory,
  MediaResource,
  useSyncedMediaResource,
} from '@brightmls/nextgen.lae.flc';

interface ResoState {
  isLoading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
}

interface ResoContextType extends ResoState {
  executeQuery: <T>(resource: string, query?: any) => Promise<T>;
  refreshSession: (baseUrl: string) => Promise<boolean>;
  updateMedia: (media: MediaResource) => void;
  media: MediaResource[];
}

export interface MediaFile {
  name: string;
  type: string;
  category: string;
  description?: string;
  shortDescription?: string;
  visibility?: string;
  itemNumber: number;
  size: number;
  base64Data?: string;
}

const REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes

export const ResoContext = createContext<ResoContextType | undefined>(
  undefined,
);

export const ResoProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<ResoState>({
    isLoading: true,
    error: null,
    isAuthenticated: false,
  });
  const {
    items: localFiles,
    updateMedia: setLocalFiles,
    displayItems: displayFiles,
  } = useSyncedMediaResource(MediaCategory.Photo);

  const { getConfigValue, supportsClientResoWebApi } = useTenant();
  const { session } = useAuth();
  const debug = useDebugger();

  const handleAuthError = useCallback(async (baseUrl: string) => {
    // Set a flag in sessionStorage to indicate RESO auth is in progress
    sessionStorage.setItem('reso_auth_in_progress', 'true');

    window.location.href = `${baseUrl}/redirect?redirectLink=${window.location.href}`;
  }, []);

  const refreshSession = useCallback(
    async (baseUrl: string): Promise<boolean> => {
      // Check if RESO is supported
      if (!supportsClientResoWebApi()) {
        // If not supported, skip refresh and set state to authenticated
        setState((prev) => ({
          ...prev,
          isAuthenticated: true,
          error: null,
          isLoading: false,
        }));
        return true;
      }

      // Check if debug mode is enabled for RESO
      const resoDebugMode = getConfigValue<boolean>('RESO_DEBUG_MODE', false);

      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await fetch(`${baseUrl}/refresh`, {
          credentials: 'include',
        });

        if (response.status === 401) {
          // Special handling for 401 - we want to trigger the redirect
          setState((prev) => ({ ...prev, isLoading: false }));
          await handleAuthError(baseUrl);
          // Never reaches here due to redirect
          return false;
        }

        if (!response.ok) {
          throw new Error('Session refresh failed');
        }

        setState((prev) => ({
          ...prev,
          isAuthenticated: true,
          error: null,
          isLoading: false,
        }));

        return true;
      } catch (error) {
        console.error('RESO refresh error:', error);

        // In debug mode, continue anyway
        if (getConfigValue<boolean>('RESO_DEBUG_MODE')) {
          debug.log(
            'ResoContext',
            'RESO refresh failed in debug mode - continuing anyway',
            'warn',
          );
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            error: null,
            isLoading: false,
          }));
          return true;
        }

        setState((prev) => ({
          ...prev,
          error: error as Error,
          isLoading: false,
        }));
        throw error; // Let the coordinator handle general errors
      }
    },
    [handleAuthError, getConfigValue, debug],
  );

  const buildResourceOptions = (resourceName: string) => {
    const baseUrl = getConfigValue<string>('RESO_BASE_URL');
    const resoTenantName = getConfigValue<string>('RESO_TENANT_NAME');

    const resource = RESO_RESOURCES[resourceName as ResoResource];

    const version = resource?.version ?? ResourceEndpointVersion.DEFAULT;
    const versionPath = version ? `/${version}` : '';

    return {
      baseUrl,
      url: `${baseUrl}/OData${versionPath}/${resoTenantName}/${resource?.endpoint || resourceName}`,
      type:
        resource?.version === ResourceEndpointVersion.LEGACY
          ? undefined
          : 'application/json',
    };
  };

  const executeQuery = useCallback(
    async <T,>(
      resource: string,
      options: {
        method?: string;
        body?: any;
        headers?: Record<string, string>;
        queryParams?: Record<string, string | number | boolean>;
        buffer?: boolean;
        timeout?: number;
      } = {},
      pageSize?: number,
    ): Promise<T> => {
      try {
        const {
          method = 'GET',
          body,
          headers = {},
          queryParams,
          timeout,
        } = options;
        let { url, type, baseUrl } = buildResourceOptions(resource);

        if (queryParams && Object.keys(queryParams).length > 0) {
          const queryString = Object.entries(queryParams)
            .map(
              ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
            )
            .join('&');
          url += `?${queryString}`;
        }

        if (!session) {
          throw new Error('No session available');
        }

        const extraHeaders: Record<string, string> = {};

        if (type) {
          extraHeaders['Content-Type'] = type;
        }

        // Add pagination header if pageSize is specified
        if (pageSize) {
          extraHeaders['Prefer'] = `'odata.maxpagesize=${pageSize}'`;
        }

        // Set up AbortController for timeout if specified
        const abortController = timeout ? new AbortController() : undefined;
        const timeoutId = timeout
          ? setTimeout(() => abortController?.abort(), timeout)
          : undefined;

        try {
          const response = await fetch(url, {
            method,
            credentials: 'include',
            headers: {
              Accept: '*/*',
              'X-RESOWebAPIKey': session?.reso.reso_client_header_api_key,
              'X-RESOWebAPISecret': session?.reso.reso_client_header_api_secret,
              ...headers,
              ...extraHeaders,
            },
            ...(body && { body: options.buffer ? body : JSON.stringify(body) }),
            ...(abortController && { signal: abortController.signal }),
          });

          // Clear timeout if request completed successfully
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          if (response.status === 401) {
            await handleAuthError(baseUrl);
            throw new Error('Unauthorized');
          }

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          // Handle cases where response might not be JSON (like file uploads)
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            return data as T;
          }

          return response as unknown as T;
        } catch (fetchError) {
          // Clear timeout on error as well
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          // Handle abort/timeout errors
          if (
            fetchError instanceof Error &&
            (fetchError.name === 'AbortError' ||
              fetchError.message.includes('aborted'))
          ) {
            throw new Error(
              `Request timeout after ${timeout}ms: ${method} ${resource}`,
            );
          }

          throw fetchError;
        }
      } catch (error) {
        setState((prev) => ({ ...prev, error: error as Error }));
        throw error;
      }
    },
    [handleAuthError, session, buildResourceOptions],
  );

  // Set up periodic refresh
  useEffect(() => {
    if (state.isAuthenticated) {
      // Skip periodic refresh in debug mode
      if (getConfigValue<boolean>('RESO_DEBUG_MODE')) {
        debug.log(
          'ResoContext',
          'Skipping periodic refresh in debug mode',
          'info',
        );
        return;
      }

      const intervalId = setInterval(() => {
        const baseUrl = getConfigValue<string>('RESO_BASE_URL');
        refreshSession(baseUrl);
      }, REFRESH_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [state.isAuthenticated, refreshSession, getConfigValue, debug]);

  const updateMedia = useCallback(
    (updatedMedia: MediaResource) => {
      if (updatedMedia.MEDIA_KEY === null) {
        console.error(`MEDIA_KEY is null for media object: ${updatedMedia}`);
        throw new Error(`MEDIA_KEY is null for media object: ${updatedMedia}`);
      }

      setLocalFiles((previousMediaList) => {
        if (!updatedMedia?.MED_VW_KEY) {
          return previousMediaList;
        }

        const existingIndex = previousMediaList.findIndex(
          (item) => item.MED_VW_KEY === updatedMedia.MED_VW_KEY,
        );

        if (existingIndex === -1) {
          return [...previousMediaList, updatedMedia];
        }

        const previousMediaDisplayOrder =
          previousMediaList[existingIndex][MediaField.MEDIA_DISPLAY_ORDER];
        const nextMediaList = previousMediaList.slice();
        nextMediaList[existingIndex] = {
          ...updatedMedia,
          [MediaField.MEDIA_DISPLAY_ORDER]: previousMediaDisplayOrder,
        };

        return nextMediaList;
      });
    },
    [setLocalFiles],
  );

  const value = {
    ...state,
    executeQuery,
    refreshSession,
    updateMedia,
    media: localFiles,
  };

  return <ResoContext.Provider value={value}>{children}</ResoContext.Provider>;
};

export const useReso = () => {
  const context = useContext(ResoContext);
  if (context === undefined) {
    throw new Error('useReso must be used within a ResoProvider');
  }
  return context;
};
